// EntryFormDialog.jsx
import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Typography,
} from "@material-ui/core";
import { BACKEND_BASE_URL } from '../../config';
import { FormHelperText } from '@mui/material';

const EntryFormDialog = ({
  isOpen,
  onClose,
  formData,
  setFormData,
  isEditing,
  onSubmit
}) => {
  console.log(formData, "Form Data in EntryFormDialog");

  // Track the initial unlock state when dialog opens
  const [wasUnlockedInitially, setWasUnlockedInitially] = useState(false);
  const [errors, setErrors] = useState({});

  // Set initial unlock state ONLY when dialog opens (not when formData.unlock_city changes)
  useEffect(() => {
    if (isOpen) {
      setWasUnlockedInitially(formData.unlock_city || false);
      setErrors({});
    }
  }, [isOpen]); // Removed formData.unlock_city from dependency array

  // Base fields (always editable)
  const baseFields = [
    { name: "country__name", label: "Country" },
    { name: "state", label: "State" },
    { name: "county", label: "County" },
    { name: "municipality_name", label: "Town" },
    { name: "population", label: "Population", type: "number" },
    { name: "latitude", label: "Latitude", type: "number" },
    { name: "longitude", label: "Longitude", type: "number" },
  ];

  // Agreement fields (locked after unlock)
  const agreementFields = [
    { name: "explain_requests_per_user", label: "Explain Requests per User", type: "number" },
    { name: "ask_aware_requests_per_user", label: "Ask Aware Requests per User", type: "number" },
    { name: "agreement_start_date", label: "Agreement Start Date", type: "date" },
    { name: "agreement_end_date", label: "Agreement End Date", type: "date" },
    { name: "reminder_days", label: "Reminder Days", type: "number" },
  ];

  console.log("isEditing", isEditing);
  console.log("wasUnlockedInitially", wasUnlockedInitially);

  // Handle input changes (never blocked for base fields)
  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleSubmit = () => {
    let newErrors = {};

    if (formData.unlock_city && !wasUnlockedInitially) {
      if (!formData.agreement_start_date) {
        newErrors.agreement_start_date = "Start date is required when city is unlocked.";
      }
      if (!formData.agreement_end_date) {
        newErrors.agreement_end_date = "End date is required when city is unlocked.";
      }
      if (
        formData.agreement_start_date &&
        formData.agreement_end_date &&
        new Date(formData.agreement_end_date) <= new Date(formData.agreement_start_date)
      ) {
        newErrors.agreement_end_date = "End date must be after start date.";
      }
    }


    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSubmit();
  };
  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEditing ? "Edit Entry" : "Add New Entry"}</DialogTitle>
      <DialogContent>
        {/* Base Fields (always editable) */}
        {baseFields.map(({ name, label, type }) => (
          <TextField
            key={name}
            type={type || "text"}
            label={label}
            fullWidth
            value={formData[name] || ""}
            onChange={(e) => handleChange(name, e.target.value)}
            style={{ marginBottom: "16px" }}
            InputLabelProps={type === "date" ? { shrink: true } : {}}
            helperText={
              name === "state"
                ? "Note: State field is optional for countries other than US, USA, United States, and United State of America."
                : ""
            }
          />
        ))}

        {/* Unlock City Checkbox */}
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.unlock_city || false}
              onChange={(e) => handleChange("unlock_city", e.target.checked)}
              disabled={wasUnlockedInitially} // disable only if already unlocked initially
            />
          }
          label="Unlock City"
          style={{ marginBottom: "16px" }}
        />

        {/* 🔹 Notes about lock status */}
        {/* 🔹 Notes about lock status */}
        {formData.is_locked_from_partnerCity ? (
          <FormHelperText style={{ color: "red", marginBottom: "16px" }}>
            This city agreement is locked from the Partner City.
          </FormHelperText>
        ) : wasUnlockedInitially ? (
          <FormHelperText style={{ color: "#ed6c02", marginBottom: "16px" }}>
            An agreement already exists with this city. You can modify it from there.
          </FormHelperText>
        ) : null}


        {/* Agreement Fields (only shown if unlocked) */}
        {formData.unlock_city && (
          <>
            {agreementFields.map(({ name, label, type }) => (
              <TextField
                key={name}
                type={type || "text"}
                label={label}
                fullWidth
                value={formData[name] || ""}
                onChange={(e) => handleChange(name, e.target.value)}
                style={{ marginBottom: "16px" }}
                InputLabelProps={type === "date" ? { shrink: true } : {}}
                error={!!errors[name]}
                helperText={errors[name] || ""}   // 👈 SHOW ERROR HERE
                disabled={wasUnlockedInitially} // lock only agreement fields if already unlocked initially
              />
            ))}

            {/* File Upload */}
            <div style={{ marginBottom: "16px" }}>
              <Typography variant="body1" style={{ marginBottom: "8px" }}>
                Upload Agreement
              </Typography>

              {formData.attachment && typeof formData.attachment === "string" ? (
                <div style={{ marginBottom: "8px" }}>
                  <Typography variant="body2">
                    Current File:{" "}
                    <a
                      href={`${BACKEND_BASE_URL}${formData.attachment}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#1976d2" }}
                    >
                      {formData.attachment.split("/").pop()}
                    </a>
                  </Typography>

                  {!wasUnlockedInitially && (
                    <div style={{ marginTop: "8px" }}>
                      <Button
                        variant="outlined"
                        component="label"
                        style={{ marginRight: "8px" }}
                      >
                        Change File
                        <input
                          type="file"
                          accept="application/pdf"
                          hidden
                          onChange={(e) => handleChange("attachment", e.target.files[0])}
                        />
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                !wasUnlockedInitially && (
                  <input
                    type="file"
                    accept="application/pdf"
                    onChange={(e) => handleChange("attachment", e.target.files[0])}
                  />
                )
              )}

              {formData.attachment && formData.attachment instanceof File && (
                <Typography
                  variant="body2"
                  style={{ marginTop: "8px", color: "#555" }}
                >
                  Selected File: {formData.attachment.name}
                </Typography>
              )}
            </div>

            {/* Notes */}
            <TextField
              label="Notes"
              multiline
              rows={4}
              fullWidth
              value={formData.notes || ""}
              onChange={(e) => handleChange("notes", e.target.value)}
              style={{ marginBottom: "16px" }}
              disabled={wasUnlockedInitially}
            />
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} style={{ backgroundColor: "red", color: "white" }}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          style={{ backgroundColor: "#0B194A", color: "white" }}
          disabled={wasUnlockedInitially && formData.unlock_city} // disable save only if already unlocked initially
        >
          {isEditing ? "Save" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EntryFormDialog;